export const instructorGuideContent = `# Praxis — Instructor Guide

Welcome to Praxis. This guide covers everything you need to know to create tech evals, manage sessions, support your students, and deliver a great workshop experience.

---

## Table of Contents

1. [Getting Started](#1-getting-started)
2. [Your Dashboard](#2-your-dashboard)
3. [Creating a Tech Eval](#3-creating-a-tech-eval)
4. [The Tech Eval Editor](#4-the-tech-eval-editor)
5. [Managing Steps](#5-managing-steps)
6. [Content Blocks](#6-content-blocks)
7. [Publishing & Lifecycle](#7-publishing--lifecycle)
8. [Creating a Session](#8-creating-a-session)
9. [Assigning Students](#9-assigning-students)
10. [The Session Dashboard](#10-the-session-dashboard)
11. [Session Lifecycle](#11-session-lifecycle)
12. [Managing Students Mid-Session](#12-managing-students-mid-session)
13. [Present Mode](#13-present-mode)
14. [The Live Console](#14-the-live-console)
15. [Help Requests](#15-help-requests)
16. [Broadcasting Messages](#16-broadcasting-messages)
17. [Session Summary & Analytics](#17-session-summary--analytics)
18. [Exporting Data](#18-exporting-data)
19. [Desktop Management](#19-desktop-management)
20. [Tips & Shortcuts](#20-tips--shortcuts)
21. [Troubleshooting](#21-troubleshooting)

---

## 1. Getting Started

### Logging In

1. Open the Praxis URL
2. Click **Sign in with CyberArk SSO**
3. Authenticate with your corporate credentials
4. You'll land on your **Instructor Dashboard**

### First-Time Checklist

If this is your first time using Praxis, here's the typical workflow:

1. **Create a Tech Eval** — design your workshop content with steps and instructions
2. **Publish the Tech Eval** — make it available for sessions
3. **Create a Session** — schedule a session based on your tech eval
4. **Assign Students** — add students and their environment credentials
5. **Send Invitations** — students receive login credentials via email
6. **Start the Session** — go live when it's time
7. **Monitor & Support** — track progress, answer help requests, broadcast messages
8. **End the Session** — wrap up and review analytics

---

## 2. Your Dashboard

The Instructor Dashboard is your home base. It shows your tech evals, sessions, and quick actions at a glance.

### Statistics

Four cards at the top show:

| Metric | What It Shows |
|--------|---------------|
| **Total Tech Evals** | Number of tech evals you've created |
| **Total Sessions** | Number of sessions across all tech evals |
| **Total Students** | Aggregate student count across all sessions |
| **Completion Rate** | Overall completion percentage |

### Tech Evals Table

Your main working area. Shows all your tech evals with:

| Column | Description |
|--------|-------------|
| **Title** | Tech Eval name (click to open the editor) |
| **Category** | Privilege Cloud, Identity Security, PAM Self-Hosted, etc. |
| **Difficulty** | Beginner, Intermediate, Advanced, or Expert |
| **Steps** | Number of steps in the tech eval |
| **Status** | Draft, Published, or Archived |
| **Last Updated** | When it was last modified |
| **Actions** | Edit button |

- Click any column header to sort
- Use pagination controls at the bottom to navigate

### Quick Actions

Three cards below the table for fast navigation:

- **Create Tech Eval** — start a new tech eval from scratch
- **New Session** — schedule a session (requires at least one published tech eval)
- **Live Dashboard** — monitor active sessions in real-time

---

## 3. Creating a Tech Eval

Navigate to **Create Tech Eval** from the dashboard or click the **Create Tech Eval** button.

### Basic Information

| Field | Required | Description |
|-------|----------|-------------|
| **Title** | Yes | The name students will see (e.g., "Privilege Cloud Safe Management") |
| **Description** | No | A summary of what the tech eval covers |

### Configuration

| Field | Default | Description |
|-------|---------|-------------|
| **Category** | Other | Choose from: Privilege Cloud, Identity Security, PAM Self-Hosted, Secrets Manager, Endpoint Privilege Manager, Other |
| **Difficulty** | Beginner | Beginner, Intermediate, Advanced, or Expert |
| **Max Students** | — | Maximum number of students per session (1-100) |
| **Tags** | — | Comma-separated tags for organization |

### Learning Details

| Field | Description |
|-------|-------------|
| **Prerequisites** | What students should know before starting |
| **Learning Objectives** | What students will achieve by completing the tech eval |

### Settings

| Setting | Default | Description |
|---------|---------|-------------|
| **Enable help requests** | On | Students can request assistance during the tech eval |
| **Enforce step order** | On | Students must complete steps in sequence |

Click **Save as Draft** to create the tech eval and open the editor.

---

## 4. The Tech Eval Editor

The editor is where you build your tech eval content. It has three main areas:

\`\`\`
+----------+---------------------------+-------------------+
|          |                           |                   |
|  Step    |     Step Editor           |  Student Preview  |
|  List    |     (Center)              |  (Right)          |
|  (Left)  |                           |                   |
|          |                           |                   |
+----------+---------------------------+-------------------+
\`\`\`

### Header Bar

From left to right:

- **Back** — return to the dashboard
- **Tech Eval title** — click to edit inline (auto-saves after 1.5 seconds)
- **Status chip** — Draft, Published, or Archived
- **Save indicator** — shows one of:
  - "All changes saved" (green checkmark)
  - "Saving..." (spinner)
  - "Unsaved changes" (yellow dot)
  - "Save failed" (red — click to retry)
- **Settings** — open tech eval settings dialog
- **Preview toggle** — show/hide the student preview panel
- **Delete** — delete the tech eval (Draft only)
- **Publish** — publish the tech eval (Draft only)
- **Archive** — archive the tech eval (Published only)

### Step List (Left Sidebar)

The left sidebar shows all steps in order:

- Click **Add Step** at the top to create a new step
- Each step shows its number, title, type icon, and estimated time
- **Drag and drop** steps to reorder them
- **Click** a step to select it for editing
- **Hover** over a step to reveal the delete button
- The footer shows total step count and estimated duration

---

## 5. Managing Steps

### Adding a Step

Click **Add Step** or use the template picker in the center panel when no step is selected:

| Template | Description |
|----------|-------------|
| **Guided Walkthrough** | Pre-configured for remote desktop or web app steps |
| **Reference / Learn** | Introduction-style content with no environment |
| **Blank** | Empty step, configure everything yourself |

### Step Configuration

When a step is selected, the center panel shows its editor:

#### Step Title

Click the title area at the top to type or edit the step name.

#### Step Type

Choose one of three types:

| Type | What It Does |
|------|--------------|
| **Introduction** | Instructions only — no environment panel shown to students |
| **Web App** | Instructions on the left, an embedded web application on the right |
| **Remote Desktop** | Instructions on the left, a live remote desktop session on the right |

#### Completion Criteria

| Criteria | How It Works |
|----------|--------------|
| **Manual** | Student clicks "Mark Complete" when they're done |
| **Checklist-gated** | Student must check off all checklist items before the complete button activates |
| **Auto-advance** | Step completes automatically when all tasks are done (or after viewing for a few seconds if there's no checklist) |

#### Estimated Time

Set the expected duration in minutes (1-120). This shows to students as a time estimate and contributes to the total tech eval duration.

#### Environment Configuration

Appears only for **Web App** and **Remote Desktop** step types:

| Field | Description |
|-------|-------------|
| **Application URL / Remote Desktop URL** | The URL students will connect to |
| **Environment Label** | Custom label shown in the environment header (e.g., "Privilege Cloud Portal") |
| **Split** | Default panel width ratio (25%-50%) — how much space instructions take vs. environment |

### Reordering Steps

Drag the **handle icon** on the left side of any step in the sidebar to reorder. The step numbers update automatically.

### Deleting a Step

Hover over a step in the sidebar and click the **delete icon**. A confirmation dialog appears before the step is removed.

---

## 6. Content Blocks

Steps are built from content blocks. Each block is a distinct piece of content that renders in the student's instruction panel.

### Block Types

#### Text

Plain text with Markdown support. Use for paragraphs, explanations, and instructions. Supports:
- **Bold**, *italic*, \`inline code\`
- Links, bullet lists, numbered lists, blockquotes
- Template variables (see below)

#### Heading

Section headings within a step. Three levels available (H1, H2, H3). Use to organize long steps into readable sections.

#### Callout

Highlighted boxes for important information. Four variants:

| Variant | Color | Use For |
|---------|-------|---------|
| **Info** | Blue | General information, tips |
| **Warning** | Amber | Cautions, things to watch out for |
| **Tip** | Green | Helpful shortcuts, best practices |
| **Important** | Red | Critical information, must-read |

Each callout can have an optional title and a content body.

#### Code

Syntax-highlighted code blocks with a dark background. Features:
- Language label in the corner
- **Copy button** — students can copy the code with one click
- Monospace font

Specify the programming language for proper syntax highlighting.

#### Image

Inline images with configurable display:

| Option | Values |
|--------|--------|
| **Size** | Small (25%), Medium (50%), Large (75%), Full (100%) |
| **Alignment** | Left, Center, Right |
| **Border Style** | None, Shadow, Bordered |
| **Caption** | Optional text below the image |
| **Alt Text** | Accessibility description |

Students can click images to open a lightbox for a closer look. If multiple images exist on a step, arrow keys navigate between them.

#### Image Grid

Display multiple images in a grid layout. Configure 2-4 columns. Each image is clickable for the lightbox gallery.

#### Video

Embedded video with controls. Supports sizing, alignment, and an optional caption.

#### Checklist

Interactive task lists that students check off as they work. Each item has:
- A checkbox
- Task description text

Checklist items integrate with the completion criteria:
- **Checklist-gated steps** require all items checked before the student can proceed
- **Auto-advance steps** complete automatically when all items are checked
- Progress shows as "X of Y tasks complete"

#### Divider

A horizontal line to visually separate sections within a step.

### Template Variables

Use template variables in text blocks to dynamically insert student-specific credentials:

| Variable | Inserts |
|----------|---------|
| \`{{tenant_url}}\` | Student's tenant URL |
| \`{{tenant_admin_email}}\` | Student's admin email |
| \`{{tenant_admin_password}}\` | Student's admin password |
| \`{{sharing_portal_url}}\` | Student's sharing portal URL |
| \`{{skytap_portal_password}}\` | Student's Skytap portal password |

These resolve to the actual credentials assigned to each student when they view the step.

### Student Preview

Toggle the **preview panel** using the eye icon in the header. This shows a live preview of how the current step will appear to students, including:
- Content blocks rendered exactly as students see them
- Environment banner showing what will appear alongside the instructions
- Action buttons (Previous / Mark Complete)

---

## 7. Publishing & Lifecycle

Tech Evals go through three lifecycle stages:

### Draft

- Fully editable — add steps, modify content, change settings
- Not available for sessions
- Can be **published** or **deleted**

### Published

- Available for creating sessions
- Content is still editable (changes apply to new sessions)
- Cannot be deleted (must archive first)
- Can be **archived**

### Archived

- No longer available for new sessions
- Content is read-only — cannot be edited
- Existing sessions using this tech eval continue to work

### Publishing

1. Click **Publish** in the editor header
2. Confirm in the dialog
3. Status changes to Published
4. The tech eval is now available when creating sessions

### Archiving

1. Click **Archive** in the editor header
2. Confirm in the dialog
3. Status changes to Archived
4. The tech eval becomes read-only

---

## 8. Creating a Session

Navigate to **New Session** from the dashboard or sessions list. Session creation is a 2-step wizard.

### Step 1: Session Details

#### Select Tech Eval

Search and select from your published tech evals. The search field shows:
- Tech Eval title
- Step count, duration, and difficulty

After selecting, a preview card shows the tech eval's title and description.

#### Session Information

| Field | Required | Default | Description |
|-------|----------|---------|-------------|
| **Session Name** | Yes | — | A descriptive name (e.g., "March 2026 Training - Morning Session") |
| **Scheduled Date** | No | — | When the session is planned |
| **Scheduled Time** | No | 09:00 AM | Start time (appears when date is set) |
| **Duration** | No | 90 min | Expected duration (15-480 minutes) |
| **Max Students** | No | 20 | Maximum student capacity (1-100) |
| **Time Zone** | No | — | UTC, EST, PST, GMT, or IST |

#### Session Settings

| Setting | Default | Description |
|---------|---------|-------------|
| **Real-time progress tracking** | On | Track student progress through steps in real time |
| **Allow help requests** | On | Students can request instructor assistance |
| **Send automated reminders** | On | Email reminders sent before the session starts |
| **Record session** | Off | Capture session activity for post-session analysis |

Click **Continue to Student Assignment** to proceed to Step 2.

---

## 9. Assigning Students

### Three Methods

#### Add Manually

Click **Add Manually** to add students one at a time. Each student card has:

| Field | Required | Description |
|-------|----------|-------------|
| **Name** | Yes | Student's full name |
| **Email** | Yes | Student's email address (must be unique within the session) |
| **Tenant URL** | No | URL of the student's assigned environment |
| **Tenant Admin Email** | No | Login email for the tenant |
| **Tenant Admin Password** | No | Login password for the tenant |
| **Sharing Portal URL** | No | URL for the sharing portal |
| **Skytap Portal Password** | No | Password for the Skytap portal |

Each student card has:
- **Duplicate button** — copies all fields to create a similar student entry (useful when credentials share a pattern)
- **Delete button** — removes the student

#### Bulk Import (CSV)

Click **Bulk Import** to paste CSV data. Format:

\`\`\`
Name,Email,TenantURL,TenantAdminEmail,TenantAdminPassword,SharingPortalURL,SkytapPassword
\`\`\`

Only **Name** and **Email** are required. All other columns are optional. A sample CSV file is available for download from the dialog.

Example:
\`\`\`
John Doe,john@example.com,https://tenant1.example.com,admin@tenant1.com,Pass123,https://portal1.example.com,portal123
Jane Smith,jane@example.com,https://tenant2.example.com,admin@tenant2.com,Pass456,,
\`\`\`

#### Auto-Generate

Coming soon. This feature will automatically generate student accounts.

### Session Summary

A summary card at the top shows:
- Students added so far
- Maximum capacity
- Session duration
- Scheduled date

### Creating the Session

Click **Create Session & Send Invites** to:
1. Create the session with all student assignments
2. Auto-generate login credentials (username and password) for each student
3. Queue invitation emails with login details

> Students receive an email with their Praxis login credentials and a link to the platform.

---

## 10. The Session Dashboard

After creating a session, you land on its dashboard. This is the command center for managing the session.

### Session Banner

A colored banner at the top shows:
- **Session name** and **tech eval title**
- **Status chip** (color changes by state — see Session Lifecycle)
- **Present button** — enters presentation mode
- **Metadata:** scheduled date, student count, duration

### Quick Actions

Four action cards:

| Action | Description |
|--------|-------------|
| **Start Monitoring** | Opens the live console to track student progress |
| **Broadcast Message** | Send a message to all connected students |
| **Send Invitations** | Send (or re-send) login credentials to students via email |
| **View Analytics** | Open session summary and analytics |

### Tabs

#### Students Tab

Shows all enrolled students in a card grid. Each student card displays:
- Avatar with initials
- Name and email
- Progress bar with step completion count
- Status: "Not Started", "X / Y steps", or "Completed"

#### Activity Timeline

A visual timeline of session events:
- Session created
- Invitations sent
- Session started
- Session ended
- Future events shown with dotted lines

#### Session Controls

The control panel for managing the session lifecycle (see next section for details).

#### Settings Tab

Modify session settings (help requests, progress tracking, reminders, recording). Changes can be saved until the session is completed.

---

## 11. Session Lifecycle

A session progresses through these states:

\`\`\`
Scheduled → Preparing → Ready → Active → Completed
                                  ↕
                                Paused
\`\`\`

### State Transitions

| Current State | Action | Next State | What Happens |
|---------------|--------|------------|--------------|
| **Scheduled** | Click **Prepare Session** | Preparing | Desktop provisioning begins for all enrolled students |
| **Preparing** | (Automatic) | Ready | All desktops are provisioned and available |
| **Ready** | Click **Start Session** | Active | Session goes live — students can begin working |
| **Active** | Click **Pause Session** | Paused | Students see a paused state |
| **Paused** | Click **Resume Session** | Active | Students can continue working |
| **Active** | Click **End Session** | Completed | Session ends for all students |

### Session Controls Panel

The controls available depend on the current state:

**Scheduled:**
- Prepare Session button

**Preparing:**
- Spinner with "Provisioning desktops for X student(s)..."

**Ready:**
- Start Session button
- Chip showing "X desktop(s) provisioned and ready"

**Active:**
- Pause Session button
- Extend Time button (coming soon)
- End Session button

**Completed:**
- "Session has ended" indicator
- Desktop cleanup status and controls

### Ending a Session

1. Click **End Session** in the Session Controls tab
2. Confirm in the dialog: "This will end the session for all students. This action cannot be undone."
3. The session status changes to Completed
4. Students see a completion screen with their stats

---

## 12. Managing Students Mid-Session

You can manage students at any point during the session.

### Adding Students

1. Go to the **Session Controls** tab
2. Click **Add Students**
3. Fill in the student's details:
   - Full Name (required)
   - Email (required)
   - Tenant URL, Admin Email, Admin Password, Portal URL, Portal Password (optional)
4. Click **Add**

The student receives credentials and can join immediately if the session is active.

### Resetting Credentials

1. Go to the **Session Controls** tab
2. Click **Reset Credentials**
3. Select the student from the dropdown
4. Enter the new credential values
5. Click **Update**

### Exporting Student List

Click **Export Student List** in the Session Controls tab to download a CSV with student names, emails, and join dates.

### Sending Invitations

Click **Send Invitations** to send (or re-send) login credentials to all enrolled students via email. A confirmation dialog shows the student count and session name before sending.

---

## 13. Present Mode

Present Mode gives you a focused, full-screen view for delivering the tech eval alongside students. It mirrors the student experience so you can walk through steps together.

### Entering Present Mode

Click the **Present** button in the session banner on the session dashboard.

### Layout

The layout matches what students see — a split-screen with instructions on the left and the environment on the right.

#### Toolbar

- **Back button** — exit presentation mode
- **Session name** and **tech eval title**
- **Reaction feed** — shows real-time emoji reactions from students
- **Step counter** — "Step X of Y"
- **Connection status** — Live or Offline
- **Student count** — number of connected students
- **Picture-in-Picture button** — opens a floating monitoring widget

### Navigating Steps

- Use the **Previous** and **Next** buttons at the bottom of the instructions panel
- Click the step header to expand the full step list and jump to any step
- Progress dots at the top provide a quick visual overview

### Collapsing Instructions

- Click the **collapse arrow** or press **Ctrl+B** to minimize the instructions panel to a thin rail
- The rail shows numbered step circles you can click to navigate
- Click the **expand arrow** or press **Ctrl+B** again to restore

### Resizing Panels

- **Drag the divider** between panels to adjust their width (20-60%)
- **Double-click the divider** to reset to the step's default split ratio
- Your preference persists during the session

### Working with Remote Desktops

When presenting a remote desktop step:

**Pasting text into the remote desktop:**
1. Click the **Paste** button in the environment header
2. Type or paste (Ctrl+V) your text into the dialog
3. Click **Send to Remote** (or press Ctrl+Enter)

**Copying text from the remote desktop:**
1. Select text in the remote desktop and press Ctrl+C
2. Click the **Copy** button in the environment header
3. Click **Copy to Clipboard**

### Picture-in-Picture (PiP)

Click the **PiP button** in the toolbar to open a floating monitoring widget in a separate window. This lets you keep an eye on student progress while presenting. The widget shows:

- Session title and tech eval name
- Live step progress grid — see which step each student is on
- Student count with activity indicators
- Emoji reaction feed from students
- Active help requests with:
  - Student name and step
  - Message preview
  - Quick reply input
  - Dismiss button

The PiP widget stays on top of other windows, so you can present your environment while monitoring the class.

---

## 14. The Live Console

The Live Console gives you a real-time overview of every student's progress.

### Accessing the Live Console

Click **Start Monitoring** from the session dashboard quick actions.

### Overview

The console shows:

- **Session info** — name, status, student count
- **Live timer** — elapsed time since session started (HH:MM:SS)
- **Connection status** — whether the real-time connection is active

### Filter & Stats

**Filter chips** to narrow the student view:
- **All** — every enrolled student
- **Needs Help** — students with active help requests
- **Completed** — students who finished all steps
- **Behind** — students below the expected pace

**Stats cards:**
- Total Students
- Completed count
- Average Progress percentage
- Open Help Requests

### Student Progress Grid

Each student card shows:
- Avatar and name
- Email
- Progress bar with percentage
- Step count (e.g., "3 / 8 steps")
- Status badge: Working (blue), Needs Help (red), Completed (green), Not Started (grey)
- Cards highlight with an animation when a student makes progress

**Click a student card** to open a detailed drawer showing:
- Full step-by-step progress breakdown
- Checklist items status (if applicable)
- Time spent on each step
- Help request history
- Quick actions (send message, reset progress)

### Real-Time Updates

The console updates automatically via WebSocket:
- Progress changes animate on the student cards
- New help requests trigger notifications
- Emoji reactions appear in the feed
- Connection status updates in real time

---

## 15. Help Requests

When a student requests help, you can respond through the help queue.

### Accessing the Help Queue

Navigate to the help queue from the session dashboard or live console.

### Help Queue Layout

The help queue is split into two panels:

**Left panel (40%)** — list of all help requests
**Right panel (60%)** — conversation thread for the selected request

### Stats at the Top

| Metric | Description |
|--------|-------------|
| **Active Requests** | Number of open/in-progress requests |
| **Avg Response Time** | How quickly you're responding |
| **Resolved Today** | Number of resolved requests |

### Request List

Each help request card shows:
- Student name and avatar
- **Priority chip** — High (red) or Normal (orange)
- **Status chip** — Open, In Progress, or Resolved
- Time since requested (e.g., "2 mins ago")
- Step context (which step the student was on)
- Message preview (first 2 lines)
- Message count badge

Filter by priority using the **High Priority** and **Normal Priority** chips.

Resolved requests are grouped in a collapsible accordion at the bottom.

### Conversation Thread

Select a request to view the full conversation:

- **Student messages** appear on the left (blue background)
- **Your messages** appear on the right (grey background)
- Each message shows the sender name and timestamp
- Messages update in real-time — no need to refresh

#### Responding

1. Type your message in the input field at the bottom
2. Press **Enter** to send (or **Shift+Enter** for a new line)

#### Message Reactions

Hover over any message to reveal a reaction button. Choose from 8 emoji options to react to a message. Students see your reactions in real-time.

#### Instructor Notes

If the tech eval step has instructor notes (added in the editor), they appear in a collapsible section above the conversation thread. These notes are not visible to students — they're reminders you wrote for yourself when building the tech eval.

### Resolving a Request

1. Click the **Resolve** button at the top of the conversation thread
2. Optionally add a closing message
3. Click **Confirm**

The student is notified that their help request has been resolved. The conversation becomes read-only.

---

## 16. Broadcasting Messages

Send a message to every student connected to your session simultaneously.

### How to Broadcast

1. From the session dashboard, click **Broadcast Message** in the quick actions
2. Or find it in the **Session Controls** tab under Communication
3. Type your message in the dialog
4. Click **Send**

### What Students See

Students see an info banner at the top of their screen that auto-hides after 8 seconds. The message appears regardless of which step they're on.

### Use Cases

- Announce a break: "We'll take a 10-minute break. Be back at 2:30 PM."
- Give a hint: "If you're stuck on Step 3, check the policy tab in the admin console."
- Time check: "15 minutes remaining. Please wrap up your current step."

---

## 17. Session Summary & Analytics

After a session ends (or during), view the session summary for analytics.

### Accessing the Summary

Click **View Analytics** from the session dashboard quick actions.

### Session Header

A gradient card showing:
- Tech Eval title and session name
- Session date, duration, participant count, and status

### Key Metrics

| Metric | Description |
|--------|-------------|
| **Completion Rate** | Percentage of students who finished all steps |
| **Average Progress** | How far the average student progressed |
| **Help Requests** | Total help requests during the session |

### Step Completion Table

A table showing each step's completion rate:
- Step number and title
- Completion percentage (color-coded bar)
- Count of students who completed it

This helps identify which steps are most challenging — low completion rates may indicate the step needs clearer instructions.

### Student Leaderboard

A ranked list of students by performance:
- **Gold, Silver, Bronze** badges for the top 3
- Completion percentage
- Progress bar
- Time spent
- Help requests used

---

## 18. Exporting Data

### Student List (CSV)

From **Session Controls > Export Student List**, download a CSV containing:
- Student name
- Email
- Join date

### Progress Data (CSV)

From **Session Controls > Export Progress Data**, download a CSV containing:
- Student name
- Completed steps
- Total steps
- Progress percentage
- Current step

### PDF Report & Email Report

Coming soon — export the full session summary as a PDF or email it to stakeholders.

---

## 19. Desktop Management

Praxis integrates with Apache Guacamole to provide remote desktop environments for students.

### Provisioning Desktops

1. Go to the **Session Controls** tab
2. Click **Prepare Session** (while in Scheduled state)
3. Desktops are provisioned for all enrolled students
4. Status changes to **Preparing**, then **Ready** when all desktops are available
5. The chip shows "X desktop(s) provisioned and ready"

### Desktop Lifecycle

| State | Description |
|-------|-------------|
| **Not Provisioned** | Session is scheduled, no desktops created yet |
| **Provisioning** | Desktops being created (automatic after you click Prepare) |
| **Ready** | All desktops available — you can start the session |
| **Active** | Session is live, students are using their desktops |
| **Grace Period** | Session ended, desktops remain accessible until expiry time |
| **Cleaned Up** | All desktops have been destroyed |

### Grace Period

After a session ends, student desktops remain available for a grace period. Students see a message telling them when their desktop will expire.

### Cleaning Up Desktops

To immediately destroy all desktops (before the grace period ends):

1. Go to **Session Controls** (after session is completed)
2. Click **Clean Up Now**
3. Confirm in the dialog: "This will immediately destroy all student desktops. Students will lose access to their practice environments."

---

## 20. Tips & Shortcuts

### Keyboard Shortcuts

| Shortcut | Where | Action |
|----------|-------|--------|
| **Ctrl+B** | Present Mode | Toggle instructions panel collapse/expand |
| **Ctrl+S** | Tech Eval Editor | Force save |
| **Ctrl+Enter** | Paste Dialog | Send text to remote desktop |
| **Enter** | Help Queue input | Send message |
| **Shift+Enter** | Any multi-line input | New line without sending |
| **Escape** | Any dialog | Close the dialog |
| **Arrow keys** | Image lightbox | Navigate between images |

### Best Practices

**Tech Eval Design**
- Keep steps focused — one concept or task per step
- Use callout blocks for critical information (students often skim body text)
- Add checklists for steps with multiple sub-tasks — it helps students track their own progress
- Use template variables (\`{{tenant_url}}\`, etc.) instead of hardcoding URLs in instructions
- Set realistic time estimates — they help students pace themselves
- Write instructor notes for steps where you anticipate questions

**Session Management**
- Send invitations at least a day before the session so students can test their login
- Prepare (provision desktops) before the scheduled start time — provisioning can take a few minutes
- Use broadcast messages to keep the class in sync
- Monitor the help queue during the session — quick responses prevent students from getting stuck
- Review the step completion table after each session to identify content that needs improvement

**Presenting**
- Use the PiP widget to monitor student progress while sharing your screen
- Collapse the instructions panel when demonstrating in the remote desktop
- Use the copy/paste buttons instead of Ctrl+C/V — clipboard doesn't cross the browser boundary directly

---

## 21. Troubleshooting

### Students Can't Log In

- Verify their email is correctly entered in the student assignment
- Re-send invitations from the Session Controls tab
- Check that the session status is **Active** (students can log in any time, but can only access the tech eval when the session is active)

### Desktops Won't Provision

- Check that all students have been assigned to the session
- Verify the Guacamole integration is configured (Admin > Settings)
- Try preparing the session again from Session Controls

### Students Show "Session Hasn't Started Yet"

The session is still in **Scheduled** or **Preparing** state. Complete the lifecycle:
1. Click **Prepare Session** (if Scheduled)
2. Wait for provisioning to complete
3. Click **Start Session** (when Ready)

### Help Requests Not Appearing

- Verify "Allow help requests" is enabled in session settings
- Check your WebSocket connection status (should show "Live" in the console)
- Refresh the help queue page

### Changes Not Saving in the Editor

- Check the save indicator in the header — if it shows "Save failed", click it to retry
- Verify your internet connection
- Changes auto-save with a 1.5-second debounce — wait a moment after editing

### Students Can't See Environment

- Verify the step type is set to **Web App** or **Remote Desktop** (not Introduction)
- Check that the URL is configured in the step's Environment Configuration
- For remote desktops: ensure the session has been prepared and desktops are provisioned

### Broadcast Messages Not Reaching Students

- Only students with an active WebSocket connection receive broadcasts
- Students who are disconnected (Offline status) will miss the message
- There is no message history for broadcasts — if a student reconnects, they won't see past broadcasts

---

**Need platform support?** Contact your system administrator or reach out to the Praxis team.
`;
